import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  FileText,
  Mail,
  Calendar,
  Settings,
  HelpCircle,
  LogOut,
  Bell,
  ChevronDown,
  Building2,
  Menu,
  X,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { cn } from '../lib/utils';

const API_URL = import.meta.env.VITE_API_URL || '';

interface LayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Conversations', href: '/conversations', icon: MessageSquare },
  { name: 'Applicants', href: '/applicants', icon: Users },
  { name: 'Forms', href: '/forms', icon: FileText },
  { name: 'Email', href: '/email', icon: Mail },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'Support', href: '/support', icon: HelpCircle },
];

export default function Layout({ children }: LayoutProps) {
  const { user, workspace, availableWorkspaces, logout, switchWorkspace, getAuthHeaders } = useAuthStore();
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('dismissed-notifications');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const location = useLocation();

  // Fetch recent activity for notifications
  const { data: notifData } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/dashboard-api/stats`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) return null;
      return res.json();
    },
    refetchInterval: 30000,
  });

  const notifications = (notifData?.recentApplicants || [])
    .slice(0, 10)
    .map((a: any) => ({
      id: a.id,
      title: `${a.first_name} ${a.last_name}`,
      subtitle: a.job_title,
      type: 'applicant' as const,
      time: a.created_at,
      status: a.status,
    }))
    .filter((n: any) => !dismissedIds.has(n.id));

  const unreadCount = notifications.length;

  const dismissNotification = (id: string) => {
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      localStorage.setItem('dismissed-notifications', JSON.stringify([...next]));
      return next;
    });
  };

  const dismissAll = () => {
    const allIds = notifications.map((n: any) => n.id);
    setDismissedIds(prev => {
      const next = new Set(prev);
      allIds.forEach((id: string) => next.add(id));
      localStorage.setItem('dismissed-notifications', JSON.stringify([...next]));
      return next;
    });
    setShowNotifications(false);
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleSwitchWorkspace = async (slug: string) => {
    if (slug === workspace?.slug) {
      setShowWorkspaceMenu(false);
      return;
    }
    await switchWorkspace(slug);
    setShowWorkspaceMenu(false);
    window.location.reload();
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-semibold text-gray-900">iHireFast</span>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-1 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Workspace Switcher */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="relative">
          <button
            onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4 text-primary-600" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="font-medium text-gray-900 truncate">{workspace?.name}</p>
              <p className="text-xs text-gray-500 capitalize">{workspace?.role}</p>
            </div>
            <ChevronDown className={cn('w-4 h-4 text-gray-400 transition-transform', showWorkspaceMenu && 'rotate-180')} />
          </button>

          {showWorkspaceMenu && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              {availableWorkspaces.map((ws) => (
                <button
                  key={ws.id}
                  onClick={() => handleSwitchWorkspace(ws.slug)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50',
                    ws.id === workspace?.id && 'bg-primary-50 text-primary-700'
                  )}
                >
                  <div className="w-6 h-6 bg-primary-100 rounded flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-3 h-3 text-primary-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{ws.name}</p>
                  </div>
                  {ws.id === workspace?.id && (
                    <div className="w-2 h-2 bg-primary-600 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href));

          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={cn(
                'flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
                isActive
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-100'
              )}
            >
              <Icon className={cn('w-5 h-5', isActive ? 'text-primary-600' : 'text-gray-400')} />
              {item.name}
            </NavLink>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-3 mb-3 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-default">
          <div className="w-9 h-9 bg-primary-600 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-sm font-semibold text-white">
                {user?.firstName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || '?'}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.firstName || user?.lastName
                ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
                : user?.email || 'User'}
            </p>
            {user?.firstName || user?.lastName ? (
              <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
            ) : workspace?.name ? (
              <p className="text-xs text-gray-500 truncate">{workspace.name}</p>
            ) : null}
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors w-full"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - desktop: fixed, mobile: slide-over */}
      <div className={cn(
        'fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 z-50 transition-transform duration-200',
        'lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {sidebarContent}
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between h-14 px-4 lg:h-16 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-lg lg:text-xl font-semibold text-gray-900 truncate">
                {navigation.find(n => location.pathname === n.href ||
                  (n.href !== '/' && location.pathname.startsWith(n.href)))?.name || 'Dashboard'}
              </h1>
            </div>
            <div className="flex items-center gap-4 relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white px-1">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setShowNotifications(false)} />
                  <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-30 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-900 text-sm">Notifications</h3>
                      {unreadCount > 0 && (
                        <button onClick={dismissAll} className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="py-8 text-center">
                          <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                          <p className="text-sm text-gray-400">All caught up!</p>
                        </div>
                      ) : (
                        notifications.map((n: any) => (
                          <div
                            key={n.id}
                            className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                          >
                            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Users className="w-4 h-4 text-primary-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                New applicant: {n.title}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{n.subtitle}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{timeAgo(n.time)}</p>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); dismissNotification(n.id); }}
                              className="text-gray-300 hover:text-gray-500 flex-shrink-0 mt-1"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                      <button
                        onClick={() => { setShowNotifications(false); }}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium w-full text-center"
                      >
                        View all applicants
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>

      {/* Click outside to close workspace menu */}
      {showWorkspaceMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowWorkspaceMenu(false)}
        />
      )}
    </div>
  );
}
